import { Modal } from "../../../shared/ui/Modal";
import { useMemo, useState } from "react";
import styles from "./AddPipelineModal.module.scss";
import { NormalButton } from "../../../shared/ui/Button";
import { reportErrorToSentry } from "@/frontend/shared/lib/reportErrorToSentry/reportErrorToSentry";
import { snackbarConstructor } from "@/frontend/shared/ui/CustomNotifications/ui/Snackbar/snackbarConstructor";
import { AxiosError } from "axios";
import { Input } from "@/frontend/shared/ui/Input";
import { postCreatePipeline } from "../model/services/postCreatePipeline";
import { CancelButton } from "@/frontend/shared/ui/Button/CancelButton/CancelButton";
import { TPipeline } from "@/frontend/pages/DashboardPage/model/types/types";

type TAddPipelineModalProps = {
  isOpen: boolean;
  onClose: () => void;
  triggerRefetch: () => void;
  pipelines: TPipeline[];
  setCurrentPipeline: (pipeline: TPipeline) => void;
};

export function AddPipelineModal({
  isOpen,
  onClose,
  triggerRefetch,
  pipelines,
  setCurrentPipeline
}: TAddPipelineModalProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [pipelineName, setPipelineName] = useState("");
  const [inputError, setInputError] = useState("");

  function validate(name: string) {
    let hasErrors = false;
    if (name.length === 0) {
      setInputError("Pipeline name is required");
      hasErrors = true;
    }

    if (pipelines.map((pipeline) => pipeline.name).includes(name)) {
      setInputError("Pipeline with this name already exists");
      hasErrors = true;
    }

    if (name.length > 30) {
      setInputError("Name is too long");
      hasErrors = true;
    }

    return hasErrors;
  }

  async function addPipelineHandler() {
    const newPipeline = pipelineName.trim();
    const errors = validate(newPipeline);
    if (errors) {
      return;
    }

    try {
      setIsLoading(true);
      const response = await postCreatePipeline({ name: newPipeline });
      triggerRefetch();
      setCurrentPipeline(response ?? pipelines?.[0] ?? null);
      snackbarConstructor(`Pipeline ${newPipeline} was added successfully.`, "normal");
      onCloseModalHandler();
    } catch (error) {
      const axiosError = error as AxiosError<{ message: any }>;
      console.error(error);
      snackbarConstructor("Something went wrong", "error");
      reportErrorToSentry(axiosError?.response?.data.message ?? "Something went wrong", `/pipelines`);
    } finally {
      setIsLoading(false);
    }
  }

  function onCloseModalHandler() {
    onClose();
    setInputError("");
    setPipelineName("");
  }

  const isSubmitDisabled = isLoading || pipelineName.length === 0;

  function onInputChangeHandler(value: string) {
    setInputError("");
    setPipelineName(value);
  }

  return (
    <div>
      <Modal isOpen={isOpen} onClose={onCloseModalHandler} title="Add Pipeline" isHeader minHeight="256px">
        <div>
          <div className={styles.newStageContainer}>
            <Input
              value={pipelineName}
              error={inputError}
              onChange={(e) => onInputChangeHandler(e.target.value)}
              placeholder="Pipeline name"
              inputSize="small"
            />
          </div>
        </div>
        <div className={styles.optionContainer}>
          <CancelButton size="large" onClick={onCloseModalHandler}>
            Cancel
          </CancelButton>
          <NormalButton size="large" onClick={addPipelineHandler} isDisabled={isSubmitDisabled}>
            Add Pipeline
          </NormalButton>
        </div>
      </Modal>
    </div>
  );
}
